import { StyleSheet, Text, View } from '@react-pdf/renderer';

import { formatMoney } from '../../utility/helpers/commonFunction';
import { getTaxSummaryCellValue, getTaxSummaryColumns, TAX_SUMMARY_COLUMN_GAP } from './taxSummaryColumns';

const styles = StyleSheet.create({
    container: {
        marginTop: 12,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#E0E0E0',
        borderStyle: 'solid',
        overflow: 'hidden',
    },
    headingBar: {
        backgroundColor: '#F8F8F8',
        padding: 6,
        marginBottom: 6,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
    },
    headingText: {
        color: '#000000',
        fontFamily: 'Roboto-500',
        fontSize: 9,
        fontWeight: 500,
    },
    headerRow: {
        flexDirection: 'row',
        paddingLeft: 6,
        paddingRight: 6,
        alignItems: 'center',
        gap: TAX_SUMMARY_COLUMN_GAP,
    },
    headerCellText: {
        color: '#000000',
        fontFamily: 'Roboto-500',
        fontSize: 9,
        fontWeight: 500,
    },
    bodyRow: {
        flexDirection: 'row',
        paddingTop: 6,
        paddingLeft: 6,
        paddingRight: 6,
        alignItems: 'center',
        gap: TAX_SUMMARY_COLUMN_GAP,
    },
    cellText: {
        color: '#000000',
        fontFamily: 'Roboto-400',
        fontSize: 9,
        fontWeight: 400,
    },
    divider: {
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
        marginTop: 8,
        marginBottom: 8,
        marginLeft: 6,
        marginRight: 6,
    },
    totalRow: {
        flexDirection: 'row',
        paddingLeft: 6,
        paddingRight: 6,
        paddingBottom: 8,
        alignItems: 'center',
        gap: TAX_SUMMARY_COLUMN_GAP,
    },
    totalCellText: {
        color: '#000000',
        fontFamily: 'Roboto-500',
        fontSize: 9,
        fontWeight: 500,
    },
});

/**
 * Tax summary table used by Invoice Design 1 and 3.
 * US shops get an extra Tax Code column; every other country uses the 5-column layout.
 */
const TaxSummaryTable = ({ summaryTaxes = [], totalTax = '$0.00', countryCode }) => {
    if (!summaryTaxes || summaryTaxes.length === 0) return null;

    const isUsShop =
        String(countryCode || '')
            .trim()
            .toUpperCase() === 'US';

    const columns = getTaxSummaryColumns(isUsShop);

    return (
        <View style={styles.container} wrap={false}>
            <View style={styles.headingBar}>
                <Text style={styles.headingText}>Tax Summary</Text>
            </View>

            <View style={styles.headerRow}>
                {columns.map((column) => (
                    <Text key={column.key} style={[column.style, { textAlign: column.align || 'left' }, styles.headerCellText]}>
                        {column.label}
                    </Text>
                ))}
            </View>

            {summaryTaxes.map((tax, index) => (
                <View key={tax?._id || tax?.taxId || index} style={styles.bodyRow}>
                    {columns.map((column) => (
                        <Text key={column.key} style={[column.style, { textAlign: column.align || 'left' }, styles.cellText]}>
                            {getTaxSummaryCellValue(column, tax)}
                        </Text>
                    ))}
                </View>
            ))}

            <View style={styles.divider} />

            {/* Mirrors the column set so "Total Tax" lands under the Tax Amount column */}
            <View style={styles.totalRow}>
                {columns.map((column, index) => (
                    <Text key={column.key} style={[column.style, { textAlign: column.align || 'left' }, styles.totalCellText]}>
                        {column.key === 'amount' ? formatMoney(totalTax) : index === 0 ? 'Total Tax' : ''}
                    </Text>
                ))}
            </View>
        </View>
    );
};

export default TaxSummaryTable;
